import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useLang } from "../i18n";

export const ArticleCard = ({ a, index = 0, t }) => (
  <Reveal delay={index * 0.07} className="h-full">
    <Link
      to={`/blog/${a.slug}`}
      data-testid={`article-card-${index}`}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
    >
      {a.cover_url ? (
        <div className="h-44 overflow-hidden">
          <img src={a.cover_url} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        </div>
      ) : (
        <div className="flex h-44 items-center justify-center bg-gradient-to-br from-navy to-azure/70">
          <span className="px-8 text-center font-serif text-xl font-semibold italic text-white/90">{a.title}</span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-7">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy/45">
          <Calendar size={12} className="text-gold" /> {(a.created_at || "").slice(0, 10)} · {a.lang.toUpperCase()}
        </p>
        <h3 className="mt-2 font-serif text-xl font-semibold leading-snug text-navy transition-colors group-hover:text-azure">
          {a.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-navy/60">{a.excerpt}</p>
        <span className="mt-5 flex items-center gap-2 text-sm font-bold text-azure">
          {t("blog.read")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  </Reveal>
);

export default function Blog() {
  const { t } = useLang();
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get("/articles?limit=50").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  return (
    <div className="min-h-screen bg-sand" data-testid="blog-page">
      <Seo
        title="Blog CabNow — Chauffeur privé Cannes, Mougins & Côte d'Azur"
        description="VTC ou taxi à Cannes et Mougins, prix des transferts aéroport Nice, conseils de chauffeurs privés : tous nos guides pour voyager sereinement sur la Riviera."
        path="/blog"
      />
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:px-8">
        <Reveal>
          <p className="overline-label mb-4">{t("blog.overline")}</p>
          <h1 className="max-w-2xl font-serif text-3xl font-semibold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            {t("blog.title")}
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-navy/60">{t("blog.subtitle")}</p>
        </Reveal>

        {articles === null ? (
          <div className="mt-16 text-center text-navy/40">…</div>
        ) : articles.length === 0 ? (
          <p className="mt-16 rounded-3xl border border-dashed border-slate-300 p-12 text-center text-sm text-navy/50" data-testid="blog-empty">
            {t("blog.empty")}
          </p>
        ) : (
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {articles.map((a, i) => (
              <ArticleCard key={a.id} a={a} index={i} t={t} />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
